import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarDaysIcon,
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  AGE_BUCKETS,
  CTC_BUCKETS,
  EXPERIENCE_BUCKETS,
} from './screeningConstants';
import { getCandidatesTableLayout } from './candidatesTableLayout';
import {
  GENERIC_STATUS_OPTIONS,
  buildQualifyingStatusOptions,
  isStatusFilterActive,
  statusFilterValues,
  usesQualifyingStatusFilter,
} from './candidateStatusFilter';

const SCORE_MIN_OPTIONS = [50, 60, 70, 80, 90];
const PROCTORING_OPTIONS = [
  { value: 'Compliant', label: 'Compliant' },
  { value: 'Flagged', label: 'Flagged' },
  { value: 'High Risk', label: 'High Risk' },
  { value: 'No data', label: 'No data' },
];
const NUMERIC_FILTER_MODES = [
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'between', label: 'Between' },
  { value: 'eq', label: 'Equal to' },
];

const EDUCATION_LABELS = {
  Doctorates: 'Doctorate / PhD',
  Masters: "Master's Degree",
  Bachelors: "Bachelor's Degree",
  Associates: "Associate's Degree",
  Other: 'Other',
};

const GENDER_LABELS = {
  male: 'Male',
  female: 'Female',
  non_binary: 'Non-binary',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
};

function ensurePortalTarget() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('candidates-filters-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'candidates-filters-portal';
    document.body.appendChild(el);
  }
  return el;
}

function buildBucketOptions(values, buckets) {
  const counts = new Map();
  values.forEach((n) => {
    if (n == null || Number.isNaN(Number(n))) return;
    const v = Number(n);
    const bucket = buckets.find((b) => v >= b.min && v <= b.max);
    if (!bucket) return;
    counts.set(bucket.id, (counts.get(bucket.id) || 0) + 1);
  });
  return buckets
    .filter((b) => counts.has(b.id))
    .map((b) => ({ value: b.id, label: b.label, count: counts.get(b.id) || 0 }));
}

function buildCountedOptions(values, labelMap) {
  const counts = new Map();
  values.forEach((v) => {
    if (v == null) return;
    const key = String(v).trim();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([value, count]) => ({
      value,
      label: labelMap?.[value] || value,
      count,
    }));
}

function buildCountedOptionsFromArrays(arrayOfArrays) {
  const counts = new Map();
  arrayOfArrays.forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((v) => {
      const key = String(v).trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([value, count]) => ({
      value,
      label: value,
      count,
    }));
}

function buildDistinctOptions(values) {
  return Array.from(
    new Set(
      values
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter((s) => s && s.length > 0)
    )
  )
    .sort((a, b) => a.localeCompare(b))
    .map((s) => ({ value: s, label: s }));
}

function RadioList({ name, value, options, onChange, compact }) {
  return (
    <div className={compact ? 'grid grid-cols-2 gap-2' : 'space-y-1.5'}>
      {options.map((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt?.value;
        const optLabel = typeof opt === 'string' ? opt : opt?.label ?? optValue;
        const active = value === optValue;
        return (
          <label
            key={String(optValue)}
            className={`flex w-full cursor-pointer items-center ${
              compact ? 'gap-2 rounded-lg px-2.5 py-2' : 'gap-3 rounded-lg px-3 py-2.5'
            } text-sm font-medium transition-all duration-150 ${
              active
                ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={String(optValue)}
              checked={active}
              onChange={() => onChange(optValue)}
              className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0 cursor-pointer accent-[#007BFF]`}
            />
            <span className="flex-1 text-left">{optLabel}</span>
          </label>
        );
      })}
    </div>
  );
}

function CheckboxListWithSearch({ items, selected, onChange, searchPlaceholder, maxVisible = 200 }) {
  const [search, setSearch] = useState('');
  const showSearch = items.length > 6;

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items.slice(0, maxVisible);
    const q = search.trim().toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(q)).slice(0, maxVisible);
  }, [items, search, maxVisible]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  if (items.length === 0) {
    return <p className="text-sm text-gray-400 italic py-4">No data available</p>;
  }

  return (
    <div className="space-y-2">
      {showSearch && (
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder || 'Search...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 placeholder:text-gray-400"
          />
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pb-1">
          {selected.map((val) => {
            const item = items.find((i) => i.value === val);
            return (
              <span
                key={val}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium"
              >
                {item?.label || val}
                <button
                  type="button"
                  onClick={() => toggle(val)}
                  className="hover:text-blue-900 transition-colors"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="max-h-56 overflow-y-auto space-y-0.5 -mx-1 px-1">
        {filteredItems.map((item) => {
          const checked = selected.includes(item.value);
          return (
            <label
              key={item.value}
              className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all duration-150 ${
                checked
                  ? 'bg-blue-50/70 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(item.value)}
                className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-[#007BFF] focus:ring-blue-200"
              />
              <span className="flex-1 text-left truncate">{item.label}</span>
              <span className="text-xs text-gray-400 font-medium tabular-nums">{item.count}</span>
            </label>
          );
        })}
        {filteredItems.length === 0 && search.trim() && (
          <p className="text-sm text-gray-400 italic py-3 text-center">No matches found</p>
        )}
      </div>
    </div>
  );
}

function KeyButton({ active, label, hasValue, onClick, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150 ${
        active
          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <span className="flex-1 text-left">{label}</span>
      {hasValue ? (
        <span
          className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
            active ? 'bg-blue-600' : 'bg-blue-500'
          }`}
          aria-hidden
        />
      ) : null}
      {count != null && count > 0 && (
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
            active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function SectionHeader({ label }) {
  return (
    <div className="px-3 pt-3 pb-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
    </div>
  );
}

function isFilled(v) {
  return v != null && String(v).trim() !== '';
}

function isNumericFilterActive(f) {
  if (!f || !f.mode) return false;
  if (f.mode === 'between') return f.min != null && f.max != null;
  return f.value != null;
}

function clampNumberLike(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function NumericModeAndInputs({ title, unitLabel, filter, onChange }) {
  const mode = filter?.mode || null;
  const value = filter?.value ?? null;
  const min = filter?.min ?? null;
  const max = filter?.max ?? null;

  return (
    <>
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="space-y-4">
        <RadioList
          name={`numeric-mode-${title}`}
          value={mode}
          options={NUMERIC_FILTER_MODES}
          compact
          onChange={(v) => {
            if (v === 'between') {
              onChange({ mode: v, value: null, min: min, max: max });
            } else {
              onChange({ mode: v, value: value, min: null, max: null });
            }
          }}
        />

        {mode === 'between' ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">
                Min{unitLabel ? ` (${unitLabel})` : ''}
              </span>
              <input
                type="number"
                value={min ?? ''}
                onChange={(e) =>
                  onChange({ mode: 'between', value: null, min: clampNumberLike(e.target.value), max })
                }
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">
                Max{unitLabel ? ` (${unitLabel})` : ''}
              </span>
              <input
                type="number"
                value={max ?? ''}
                onChange={(e) =>
                  onChange({ mode: 'between', value: null, min, max: clampNumberLike(e.target.value) })
                }
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </label>
          </div>
        ) : mode ? (
          <label className="block max-w-[220px]">
            <span className="text-xs font-medium text-gray-500">
              {mode === 'min' ? 'Min' : mode === 'max' ? 'Max' : 'Equal to'}
              {unitLabel ? ` (${unitLabel})` : ''}
            </span>
            <input
              type="number"
              value={value ?? ''}
              onChange={(e) =>
                onChange({ mode, value: clampNumberLike(e.target.value), min: null, max: null })
              }
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            />
          </label>
        ) : null}
      </div>
    </>
  );
}

const NUMERIC_FILTER_MODES_WITH_DESC = [
  { value: 'min', label: 'Min', desc: 'At least this score' },
  { value: 'max', label: 'Max', desc: 'At most this score' },
  { value: 'between', label: 'Between', desc: 'Score in a range' },
  { value: 'eq', label: 'Equal to', desc: 'Exact score match' },
];

const MAX_PER_QUESTION_FILTERS = 5;

function truncateText(s, n) {
  if (!s) return '';
  const str = String(s);
  if (str.length <= n) return str;
  return str.slice(0, n).trim() + '…';
}

function ModeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = NUMERIC_FILTER_MODES_WITH_DESC.find((m) => m.value === value) || null;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
      >
        <span className="flex-1 text-left">
          {selected ? (
            <span className="font-medium text-gray-900">{selected.label}</span>
          ) : (
            <span className="text-gray-400">Select mode…</span>
          )}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="py-1">
            {NUMERIC_FILTER_MODES_WITH_DESC.map((opt) => {
              const isSelected = value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`w-full flex flex-col items-start gap-0.5 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm font-medium">{opt.label}</span>
                  <span className="text-[11px] text-gray-400 leading-snug">{opt.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function InterviewScoreFilter({ filter, onChange }) {
  const mode = filter?.mode || null;
  const value = filter?.value ?? null;
  const min = filter?.min ?? null;
  const max = filter?.max ?? null;

  const handleModeChange = (v) => {
    if (v === 'between') {
      onChange({ mode: v, value: null, min, max });
    } else {
      onChange({ mode: v, value, min: null, max: null });
    }
  };

  return (
    <>
      <h4 className="text-sm font-semibold text-gray-900 mb-3">Interview Score</h4>
      <div className="flex items-start gap-4">
        {/* Left: mode dropdown */}
        <div className="flex-1 min-w-0">
          <span className="block text-xs font-medium text-gray-500 mb-1">Filter type</span>
          <ModeDropdown value={mode} onChange={handleModeChange} />
        </div>

        {/* Right: value input(s) */}
        <div className="flex-1 min-w-0">
          {mode === 'between' ? (
            <div className="space-y-2">
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Min (%)</span>
                <input
                  type="number"
                  value={min ?? ''}
                  onChange={(e) =>
                    onChange({ mode: 'between', value: null, min: clampNumberLike(e.target.value), max })
                  }
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Max (%)</span>
                <input
                  type="number"
                  value={max ?? ''}
                  onChange={(e) =>
                    onChange({ mode: 'between', value: null, min, max: clampNumberLike(e.target.value) })
                  }
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
              </label>
            </div>
          ) : mode ? (
            <label className="block">
              <span className="text-xs font-medium text-gray-500">
                {mode === 'min' ? 'Min (%)' : mode === 'max' ? 'Max (%)' : 'Value (%)'}
              </span>
              <input
                type="number"
                value={value ?? ''}
                onChange={(e) =>
                  onChange({ mode, value: clampNumberLike(e.target.value), min: null, max: null })
                }
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
            </label>
          ) : (
            <div className="mt-6 text-xs text-gray-400 italic"></div>
          )}
        </div>
      </div>
    </>
  );
}

function AdvancedInterviewFilter({ interviewQuestions, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const usedIndexes = new Set(
    items
      .map((it) => (it && typeof it.index === 'number' ? it.index : null))
      .filter((v) => v != null)
  );
  const availableQuestions = (interviewQuestions || []).filter(
    (q) => q && typeof q.index === 'number'
  );
  const allUsed =
    availableQuestions.length > 0 && usedIndexes.size >= availableQuestions.length;
  const atCap = items.length >= MAX_PER_QUESTION_FILTERS;

  const updateRow = (rowIdx, patch) => {
    const next = items.map((it, i) => (i === rowIdx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const removeRow = (rowIdx) => {
    onChange(items.filter((_, i) => i !== rowIdx));
  };
  const addRow = () => {
    if (atCap || allUsed) return;
    onChange([...items, { index: null, min: null }]);
  };

  if (!availableQuestions.length) {
    return (
      <div className="mt-6 pt-5 border-t border-gray-100">
        <p className="text-sm font-semibold text-gray-700">
          Advanced Interview Filter
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Interview questions will appear here once the screening's interview is configured.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-5 border-t border-gray-100">
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-900">
          Advanced Interview Filter
        </p>
        <p className="mt-0.5 text-[11px] text-gray-500 leading-snug">
          Show only candidates who scored above your chosen mark on the questions you pick.
        </p>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-xs text-gray-400 italic">No question filters added.</p>
        )}

        {items.map((row, i) => {
          const selected = availableQuestions.find((q) => q.index === row?.index) || null;
          return (
            <div
              key={i}
              className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2.5"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    Question
                  </label>
                  <QuestionDropdown
                    questions={availableQuestions}
                    selectedIndex={row?.index}
                    disabledIndexes={Array.from(usedIndexes).filter(
                      (idx) => idx !== row?.index
                    )}
                    onSelect={(idx) => updateRow(i, { index: idx })}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="mt-5 inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Remove question filter"
                  title="Remove"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-end gap-2">
                <label className="block flex-1 max-w-[180px]">
                  <span className="text-[11px] font-medium text-gray-500">Minimum score</span>
                  <div className="mt-1 relative">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="e.g. 80"
                      value={row?.min ?? ''}
                      onChange={(e) =>
                        updateRow(i, {
                          min: clampNumberLike(e.target.value),
                        })
                      }
                      className="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">
                      %
                    </span>
                  </div>
                </label>
                {selected ? (
                  <span className="text-[11px] text-gray-500 pb-2">
                    Show candidates scoring{' '}
                    <span className="font-semibold text-gray-700">
                      ≥ {row?.min != null ? row.min : '—'}%
                    </span>{' '}
                    on this question.
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={addRow}
          disabled={atCap || allUsed}
          className={`w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed py-2.5 text-sm font-medium transition-colors ${
            atCap || allUsed
              ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50/40'
              : 'border-blue-200 text-blue-600 bg-blue-50/40 hover:bg-blue-50'
          }`}
        >
          <PlusIcon className="h-4 w-4" />
          {items.length === 0 ? 'Add question filter' : 'Add another question'}
          {atCap ? (
            <span className="text-[10px] font-normal text-gray-400 ml-1">(max {MAX_PER_QUESTION_FILTERS})</span>
          ) : null}
        </button>
      </div>
    </div>
  );
}

function QuestionDropdown({ questions, selectedIndex, disabledIndexes, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const disabledSet = new Set(disabledIndexes || []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = questions.find((q) => q.index === selectedIndex) || null;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter(
      (it) =>
        (it.prompt || '').toLowerCase().includes(q) ||
        `q${(it.index ?? 0) + 1}`.includes(q)
    );
  }, [questions, search]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
        title={selected?.prompt || 'Select a question'}
      >
        <span className="flex-1 text-left truncate text-gray-800">
          {selected ? (
            <>
              <span className="font-semibold text-gray-900">
                Q{(selected.index ?? 0) + 1}
              </span>
              <span className="mx-1.5 text-gray-300">·</span>
              <span>{truncateText(selected.prompt, 56)}</span>
            </>
          ) : (
            <span className="text-gray-400">Select a question…</span>
          )}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          {questions.length > 5 && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search questions…"
                  className="w-full pl-8 pr-2 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 italic text-center">
                No matches
              </p>
            )}
            {filtered.map((q) => {
              const isDisabled = disabledSet.has(q.index);
              const isSelected = selectedIndex === q.index;
              return (
                <button
                  key={q.index}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    onSelect(q.index);
                    setOpen(false);
                    setSearch('');
                  }}
                  className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                    isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                        ? 'bg-blue-50 text-blue-800'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={q.prompt}
                >
                  <span className="font-semibold flex-shrink-0 w-8">
                    Q{(q.index ?? 0) + 1}
                  </span>
                  <span className="flex-1 truncate">{truncateText(q.prompt, 80)}</span>
                  {isDisabled && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0">already added</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCountPill({ rows, draftScoreFilters, draftMetaFilters, filterRowsWith }) {
  const total = Array.isArray(rows) ? rows.length : 0;
  const matched = useMemo(() => {
    if (typeof filterRowsWith !== 'function' || !Array.isArray(rows)) return total;
    try {
      return filterRowsWith(rows, draftScoreFilters, draftMetaFilters).length;
    } catch {
      return total;
    }
  }, [rows, draftScoreFilters, draftMetaFilters, filterRowsWith, total]);

  if (!total) return <div className="flex-1" />;
  const isFiltered = matched !== total;
  const isEmpty = matched === 0;
  const dotClass = isEmpty
    ? 'bg-amber-500'
    : isFiltered
      ? 'bg-emerald-500'
      : 'bg-gray-300';

  return (
    <div className="flex-1 flex justify-center">
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-[12px] text-gray-600"
        title={isEmpty ? 'No candidates match — try lowering thresholds' : 'Live preview of how many candidates match the current filters'}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} aria-hidden />
        <span className="tabular-nums">
          <span className="font-semibold text-gray-900">{matched}</span>
          <span className="text-gray-400"> of </span>
          <span className="text-gray-700">{total}</span>
          <span className="text-gray-500"> candidates match</span>
        </span>
      </div>
    </div>
  );
}

export default function CandidatesFiltersModal({
  isOpen,
  onClose,
  anchorRef,
  screeningMeta,
  tableOptions,
  rows,
  parseExperienceYearsForFilter,
  parseAgeYearsForFilter,
  parseCurrentCtcLacsForFilter,
  scoreFilters,
  setScoreFilters,
  metaFilters,
  setMetaFilters,
  interviewQuestions,
  filterRowsWith,
  onClearAll,
}) {
  const portalTarget = useMemo(() => ensurePortalTarget(), []);
  const panelRef = useRef(null);
  const [activeKey, setActiveKey] = useState(null);

  const [draftScoreFilters, setDraftScoreFilters] = useState(scoreFilters);
  const [draftMetaFilters, setDraftMetaFilters] = useState(metaFilters);

  useEffect(() => {
    if (!isOpen) return;
    setDraftScoreFilters(scoreFilters);
    setDraftMetaFilters(metaFilters);
  }, [isOpen, scoreFilters, metaFilters]);

  const layout = useMemo(
    () => getCandidatesTableLayout(screeningMeta, tableOptions),
    [screeningMeta, tableOptions]
  );

  // --- Build filter options from rows data ---
  const expOptions = useMemo(
    () => buildBucketOptions((rows || []).map(parseExperienceYearsForFilter), EXPERIENCE_BUCKETS),
    [rows, parseExperienceYearsForFilter]
  );
  const ctcOptions = useMemo(
    () => buildBucketOptions((rows || []).map(parseCurrentCtcLacsForFilter), CTC_BUCKETS),
    [rows, parseCurrentCtcLacsForFilter]
  );
  const qualifyingStatusFilter = usesQualifyingStatusFilter(screeningMeta);
  const qualifyingStatusOptions = useMemo(
    () => (qualifyingStatusFilter ? buildQualifyingStatusOptions(rows) : []),
    [qualifyingStatusFilter, rows]
  );
  const noticeOptions = useMemo(
    () => buildDistinctOptions((rows || []).map((c) => c?.noticePeriod)),
    [rows]
  );
  const educationOptions = useMemo(
    () => buildCountedOptions((rows || []).map((c) => c?.maxEducationLevel), EDUCATION_LABELS),
    [rows]
  );
  const skillsOptions = useMemo(
    () => buildCountedOptionsFromArrays((rows || []).map((c) => c?.skills || [])),
    [rows]
  );
  const locationOptions = useMemo(
    () => buildCountedOptions((rows || []).map((c) => c?.location)),
    [rows]
  );
  const companyOptions = useMemo(
    () => buildCountedOptions((rows || []).map((c) => c?.company)),
    [rows]
  );
  const currentRoleOptions = useMemo(
    () => buildCountedOptions((rows || []).map((c) => c?.currentRole)),
    [rows]
  );
  const genderOptions = useMemo(
    () => buildCountedOptions((rows || []).map((c) => c?.gender), GENDER_LABELS),
    [rows]
  );

  // --- Build sidebar keys ---
  const keys = useMemo(() => {
    const scoreKeys = [];
    if (tableOptions?.showStatus) scoreKeys.push({ id: 'status', label: 'Status' });
    if (layout.showOverallScoreColumn) scoreKeys.push({ id: 'overallScore', label: 'Overall' });
    if (layout.showResumeScoreColumn) scoreKeys.push({ id: 'resumeScore', label: 'Resume' });
    if (layout.showAssessmentScoreColumn)
      scoreKeys.push({ id: 'assessmentMin', label: 'Assessment' });
    if (layout.showInterviewScoreColumn)
      scoreKeys.push({ id: 'interviewScore', label: 'Interview' });
    if (layout.showProctoringColumn) scoreKeys.push({ id: 'proctoring', label: 'Proctoring' });

    const profileKeys = [];
    if (expOptions.length > 0) profileKeys.push({ id: 'experience', label: 'Experience' });
    if (educationOptions.length > 0) profileKeys.push({ id: 'education', label: 'Education' });
    if (skillsOptions.length > 0) profileKeys.push({ id: 'skills', label: 'Skills' });
    if (locationOptions.length > 0) profileKeys.push({ id: 'location', label: 'Location' });
    if (companyOptions.length > 0) profileKeys.push({ id: 'company', label: 'Current Company' });
    if (currentRoleOptions.length > 0) profileKeys.push({ id: 'currentRole', label: 'Designation' });
    if (genderOptions.length > 0) profileKeys.push({ id: 'gender', label: 'Gender' });
    if (ctcOptions.length > 0 && layout.showCurrentCtcColumn !== false) profileKeys.push({ id: 'currentCtc', label: 'Current CTC' });
    if (noticeOptions.length > 0 && layout.showNoticePeriodColumn !== false) profileKeys.push({ id: 'noticePeriod', label: 'Notice Period' });
    if (layout.showAgeColumn !== false) profileKeys.push({ id: 'age', label: 'Age' });
    profileKeys.push({ id: 'completedAt', label: 'Completed At' });

    return { scoreKeys, profileKeys, all: [...scoreKeys, ...profileKeys] };
  }, [
    layout,
    tableOptions,
    expOptions,
    ctcOptions,
    noticeOptions,
    educationOptions,
    skillsOptions,
    locationOptions,
    companyOptions,
    currentRoleOptions,
    genderOptions,
  ]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (isStatusFilterActive(scoreFilters?.status)) n += 1;
    if (isNumericFilterActive(scoreFilters?.overall)) n += 1;
    if (isNumericFilterActive(scoreFilters?.resume)) n += 1;
    if (scoreFilters?.assessmentMin != null) n += 1;
    if (isNumericFilterActive(scoreFilters?.interview)) n += 1;
    if (
      Array.isArray(scoreFilters?.interviewPerQuestion) &&
      scoreFilters.interviewPerQuestion.some(
        (q) => q && typeof q.index === 'number' && q.min != null && Number.isFinite(Number(q.min))
      )
    ) {
      n += 1;
    }
    if (metaFilters?.proctoring) n += 1;
    if (metaFilters?.experience) n += 1;
    if (isNumericFilterActive(metaFilters?.age)) n += 1;
    if (metaFilters?.currentCtc) n += 1;
    if (metaFilters?.noticePeriod) n += 1;
    if (isFilled(metaFilters?.completedAtFrom) || isFilled(metaFilters?.completedAtTo)) n += 1;
    if (metaFilters?.education?.length > 0) n += 1;
    if (metaFilters?.skills?.length > 0) n += 1;
    if (metaFilters?.location?.length > 0) n += 1;
    if (metaFilters?.company?.length > 0) n += 1;
    if (metaFilters?.currentRole?.length > 0) n += 1;
    if (metaFilters?.gender?.length > 0) n += 1;
    return n;
  }, [scoreFilters, metaFilters]);

  useEffect(() => {
    if (!isOpen) return;
    if (!activeKey && keys.all.length > 0) setActiveKey(keys.all[0].id);
  }, [isOpen, activeKey, keys.all]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !portalTarget) return null;

  const setScore = (patch) => setDraftScoreFilters((prev) => ({ ...prev, ...patch }));
  const setMeta = (patch) => setDraftMetaFilters((prev) => ({ ...prev, ...patch }));

  const emptyScoreFilters = {
    overall: { mode: null, value: null, min: null, max: null },
    resume: { mode: null, value: null, min: null, max: null },
    assessmentMin: null,
    interview: { mode: null, value: null, min: null, max: null },
    interviewPerQuestion: [],
    status: null,
  };

  const emptyMetaFilters = {
    proctoring: null,
    experience: null,
    age: { mode: null, value: null, min: null, max: null },
    currentCtc: null,
    noticePeriod: null,
    completedAtFrom: null,
    completedAtTo: null,
    education: [],
    skills: [],
    location: [],
    company: [],
    currentRole: [],
    gender: [],
  };

  const renderRightPane = () => {
    let content = null;
    let clearHandler = null;

    switch (activeKey) {
      case 'status':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Status</h4>
            {qualifyingStatusFilter ? (
              // Qualifying-only screening: the same stages the Status column shows; pick several.
              <CheckboxListWithSearch
                items={qualifyingStatusOptions}
                selected={statusFilterValues(draftScoreFilters.status)}
                onChange={(arr) => setScore({ status: arr.length > 0 ? arr : null })}
              />
            ) : (
              <RadioList
                name="filter-status"
                value={draftScoreFilters.status}
                options={GENERIC_STATUS_OPTIONS}
                onChange={(v) => setScore({ status: v })}
              />
            )}
          </>
        );
        clearHandler = () => setScore({ status: null });
        break;
      case 'overallScore':
        content = (
          <NumericModeAndInputs
            title="Overall Score"
            unitLabel="%"
            filter={draftScoreFilters.overall}
            onChange={(f) => setScore({ overall: f })}
          />
        );
        clearHandler = () => setScore({ overall: { mode: null, value: null, min: null, max: null } });
        break;
      case 'resumeScore':
        content = (
          <NumericModeAndInputs
            title="Resume Score"
            unitLabel="%"
            filter={draftScoreFilters.resume}
            onChange={(f) => setScore({ resume: f })}
          />
        );
        clearHandler = () => setScore({ resume: { mode: null, value: null, min: null, max: null } });
        break;
      case 'assessmentMin':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Assessment Score (minimum)</h4>
            <RadioList
              name="filter-assessment"
              value={draftScoreFilters.assessmentMin}
              options={SCORE_MIN_OPTIONS.map((n) => ({ value: n, label: `>${n}%` }))}
              onChange={(v) => setScore({ assessmentMin: v })}
            />
          </>
        );
        clearHandler = () => setScore({ assessmentMin: null });
        break;
      case 'interviewScore':
        content = (
          <>
            <InterviewScoreFilter
              filter={draftScoreFilters.interview}
              onChange={(f) => setScore({ interview: f })}
            />
            <AdvancedInterviewFilter
              interviewQuestions={interviewQuestions || []}
              value={draftScoreFilters.interviewPerQuestion || []}
              onChange={(arr) => setScore({ interviewPerQuestion: arr })}
            />
          </>
        );
        clearHandler = () =>
          setScore({
            interview: { mode: null, value: null, min: null, max: null },
            interviewPerQuestion: [],
          });
        break;
      case 'proctoring':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Proctoring</h4>
            <RadioList
              name="filter-proctoring"
              value={draftMetaFilters.proctoring}
              options={PROCTORING_OPTIONS}
              onChange={(v) => setMeta({ proctoring: v })}
            />
          </>
        );
        clearHandler = () => setMeta({ proctoring: null });
        break;
      case 'experience':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Experience</h4>
            <RadioList
              name="filter-experience"
              value={draftMetaFilters.experience}
              options={expOptions}
              onChange={(v) => setMeta({ experience: v })}
            />
          </>
        );
        clearHandler = () => setMeta({ experience: null });
        break;
      case 'education':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Education</h4>
            <CheckboxListWithSearch
              items={educationOptions}
              selected={draftMetaFilters.education || []}
              onChange={(arr) => setMeta({ education: arr })}
              searchPlaceholder="Search education..."
            />
          </>
        );
        clearHandler = () => setMeta({ education: [] });
        break;
      case 'skills':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Skills</h4>
            <CheckboxListWithSearch
              items={skillsOptions}
              selected={draftMetaFilters.skills || []}
              onChange={(arr) => setMeta({ skills: arr })}
              searchPlaceholder="Search skills..."
            />
          </>
        );
        clearHandler = () => setMeta({ skills: [] });
        break;
      case 'location':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Location</h4>
            <CheckboxListWithSearch
              items={locationOptions}
              selected={draftMetaFilters.location || []}
              onChange={(arr) => setMeta({ location: arr })}
              searchPlaceholder="Search location..."
            />
          </>
        );
        clearHandler = () => setMeta({ location: [] });
        break;
      case 'company':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Company</h4>
            <CheckboxListWithSearch
              items={companyOptions}
              selected={draftMetaFilters.company || []}
              onChange={(arr) => setMeta({ company: arr })}
              searchPlaceholder="Search company..."
            />
          </>
        );
        clearHandler = () => setMeta({ company: [] });
        break;
      case 'currentRole':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Designation</h4>
            <CheckboxListWithSearch
              items={currentRoleOptions}
              selected={draftMetaFilters.currentRole || []}
              onChange={(arr) => setMeta({ currentRole: arr })}
              searchPlaceholder="Search designation..."
            />
          </>
        );
        clearHandler = () => setMeta({ currentRole: [] });
        break;
      case 'gender':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Gender</h4>
            <CheckboxListWithSearch
              items={genderOptions}
              selected={draftMetaFilters.gender || []}
              onChange={(arr) => setMeta({ gender: arr })}
              searchPlaceholder="Search..."
            />
          </>
        );
        clearHandler = () => setMeta({ gender: [] });
        break;
      case 'age':
        content = (
          <NumericModeAndInputs
            title="Age"
            unitLabel="years"
            filter={draftMetaFilters.age}
            onChange={(f) => setMeta({ age: f })}
          />
        );
        clearHandler = () => setMeta({ age: { mode: null, value: null, min: null, max: null } });
        break;
      case 'currentCtc':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Current CTC</h4>
            <RadioList
              name="filter-ctc"
              value={draftMetaFilters.currentCtc}
              options={ctcOptions}
              onChange={(v) => setMeta({ currentCtc: v })}
            />
          </>
        );
        clearHandler = () => setMeta({ currentCtc: null });
        break;
      case 'noticePeriod':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Notice Period</h4>
            <RadioList
              name="filter-notice"
              value={draftMetaFilters.noticePeriod}
              options={noticeOptions}
              onChange={(v) => setMeta({ noticePeriod: v })}
            />
          </>
        );
        clearHandler = () => setMeta({ noticePeriod: null });
        break;
      case 'completedAt':
        content = (
          <>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Completed At</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-gray-500">From</span>
                  <input
                    type="date"
                    value={draftMetaFilters.completedAtFrom || ''}
                    onChange={(e) => setMeta({ completedAtFrom: e.target.value })}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-500">To</span>
                  <input
                    type="date"
                    value={draftMetaFilters.completedAtTo || ''}
                    onChange={(e) => setMeta({ completedAtTo: e.target.value })}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Last 7 days', days: 7 },
                  { label: 'Last 30 days', days: 30 },
                  { label: 'Last 90 days', days: 90 },
                ].map(({ label, days }) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => {
                      const to = new Date();
                      const from = new Date();
                      from.setDate(to.getDate() - days);
                      setMeta({
                        completedAtFrom: from.toISOString().slice(0, 10),
                        completedAtTo: to.toISOString().slice(0, 10),
                      });
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
        clearHandler = () => setMeta({ completedAtFrom: null, completedAtTo: null });
        break;
      default:
        content = <div className="text-sm text-gray-500 italic">Select a filter from the sidebar.</div>;
        break;
    }

    return (
      <div className="flex flex-col h-full">
        {clearHandler && (
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={clearHandler}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
            >
              Clear
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{content}</div>
      </div>
    );
  };

  const hasKeyValue = (keyId) => {
    switch (keyId) {
      case 'status':
        return isStatusFilterActive(scoreFilters.status);
      case 'overallScore':
        return isNumericFilterActive(scoreFilters.overall);
      case 'resumeScore':
        return isNumericFilterActive(scoreFilters.resume);
      case 'assessmentMin':
        return scoreFilters.assessmentMin != null;
      case 'interviewScore':
        return (
          isNumericFilterActive(scoreFilters.interview) ||
          (Array.isArray(scoreFilters.interviewPerQuestion) &&
            scoreFilters.interviewPerQuestion.some(
              (q) =>
                q &&
                typeof q.index === 'number' &&
                q.min != null &&
                Number.isFinite(Number(q.min))
            ))
        );
      case 'proctoring':
        return !!metaFilters.proctoring;
      case 'experience':
        return !!metaFilters.experience;
      case 'age':
        return isNumericFilterActive(metaFilters.age);
      case 'currentCtc':
        return !!metaFilters.currentCtc;
      case 'noticePeriod':
        return !!metaFilters.noticePeriod;
      case 'completedAt':
        return isFilled(metaFilters.completedAtFrom) || isFilled(metaFilters.completedAtTo);
      case 'education':
        return metaFilters.education?.length > 0;
      case 'skills':
        return metaFilters.skills?.length > 0;
      case 'location':
        return metaFilters.location?.length > 0;
      case 'company':
        return metaFilters.company?.length > 0;
      case 'currentRole':
        return metaFilters.currentRole?.length > 0;
      case 'gender':
        return metaFilters.gender?.length > 0;
      default:
        return false;
    }
  };

  return createPortal(
    <>
      <div
        role="presentation"
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(900px, 95vw)',
          maxHeight: '82vh',
        }}
        className="z-50 flex flex-col rounded-2xl bg-white shadow-2xl border border-gray-200/80 overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-blue-50">
              <FunnelIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Filters</p>
              <p className="text-[11px] text-gray-400 font-medium">
                {activeCount > 0 ? (
                  <>
                    <span className="text-blue-600">{activeCount}</span> active
                  </>
                ) : (
                  'No active filters'
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close filters"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body: Two-pane layout */}
        <div className="flex flex-1 min-h-0">
          {/* Left sidebar */}
          <div className="w-44 sm:w-52 shrink-0 border-r border-gray-100 bg-gray-50/50 overflow-y-auto">
            {keys.scoreKeys.length > 0 && (
              <>
                <SectionHeader label="Scores" />
                <div className="px-2 space-y-0.5">
                  {keys.scoreKeys.map((k) => (
                    <KeyButton
                      key={k.id}
                      active={activeKey === k.id}
                      label={k.label}
                      hasValue={hasKeyValue(k.id)}
                      onClick={() => setActiveKey(k.id)}
                    />
                  ))}
                </div>
              </>
            )}

            {keys.profileKeys.length > 0 && (
              <>
                <SectionHeader label="Candidate Profile" />
                <div className="px-2 pb-3 space-y-0.5">
                  {keys.profileKeys.map((k) => (
                    <KeyButton
                      key={k.id}
                      active={activeKey === k.id}
                      label={k.label}
                      hasValue={hasKeyValue(k.id)}
                      onClick={() => setActiveKey(k.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right pane */}
          <div className="flex-1 p-4 sm:p-5 overflow-y-auto overflow-x-hidden min-h-0">{renderRightPane()}</div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-gray-100 bg-gray-50/40">
          <button
            type="button"
            onClick={() => {
              onClearAll?.();
              setDraftScoreFilters(emptyScoreFilters);
              setDraftMetaFilters(emptyMetaFilters);
              if (keys.all.length > 0) setActiveKey(keys.all[0].id);
            }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors"
          >
            Clear all
          </button>
          <MatchCountPill
            rows={rows}
            draftScoreFilters={draftScoreFilters}
            draftMetaFilters={draftMetaFilters}
            filterRowsWith={filterRowsWith}
          />
          <button
            type="button"
            onClick={() => {
              setScoreFilters(draftScoreFilters);
              setMetaFilters(draftMetaFilters);
              onClose?.();
            }}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-[#007BFF] text-white hover:bg-[#0062CC] shadow-sm transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </>,
    portalTarget
  );
}
